import './picker.css'

import { DropDownButton } from '@progress/kendo-react-buttons';
import {
    DateTimePicker as KendoDateTimePicker,
} from '@progress/kendo-react-dateinputs';
import { DateTimePickerProps } from './props/DateTimePickerProps';
import { usePickerSharedValues } from './hooks/usePickerSharedValues';
import { useAdaptiveVisibility } from '../../hooks/useAdaptiveVisibility';
import { usePickerItems } from './hooks/usePickerItems';
import { usePickerEventListeners } from './hooks/usePickerEventListeners';
import { usePickerElementPattern } from './hooks/usePickerElementPattern';
import { usePickerValueChange } from './hooks/usePickerValueChange';
import { isNullOrEmpty } from '../../helpers/ObjectHelpers';
import { DropDownButtonActionRender } from '../render/DropDownButtonActionRender';
import Icons from '../icons/Icons';
import moment from 'moment';
import { useAdaptiveValue } from '../../hooks/useAdaptiveValue';

export const DateTimePicker = ({
    title = "",
    value = null,
    placeholder = "Velg dato",
    disabled = false,
    hasValidationError = false,
    canDelete = true,
    adaptive = true,
    showYear = false,
    showSeconds = false,
    className = "",
    onChange,
    min = new Date(2014, 1, 10, 12, 30),
    max = new Date(2050, 2, 10, 12, 30),
}: DateTimePickerProps) => {
    
    const {
        items, setItems,
        show, setShow,
        localValue, setLocalValue,
        localDisabled, setLocalDisabled
    } = usePickerSharedValues(disabled);

    // *************************
    // todo: remove this fix when KendoDateTimePicker adaptive view is fixed for react 19
    useAdaptiveVisibility(show, adaptive, disabled, setLocalDisabled);
    // *************************

    useAdaptiveValue(show, adaptive, setLocalValue, localValue, value);
    usePickerItems(canDelete, setItems, value);
    usePickerEventListeners(setShow);
    usePickerElementPattern();
    usePickerValueChange(setLocalValue, localValue, value);
    
    return (
        <div className={`date-picker ${className} ${hasValidationError ? "date-validation-error" : ""}`}
            style={{ gridTemplateColumns: "1fr auto" }}>
            <div className={`date-component`}>
                <KendoDateTimePicker
                    adaptiveTitle={isNullOrEmpty(title) ? "Velg dato" : `Velg ${title.toLowerCase()}`}
                    value={localValue}
                    placeholder={placeholder}
                    disabled={localDisabled}
                    show={show}
                    adaptive={adaptive}
                    onClose={() => setShow(false)}
                    onChange={handleOnChange}
                    className="date-size"
                    format={`dd.MM${showYear ? ".yyyy" : ""} HH:mm${showSeconds ? ":ss" : ""}`}
                    min={min!}
                    max={max!}
                />
            </div>
            <div className="date-button">
                <DropDownButton
                    className="date-time-picker-button"
                    items={items}
                    disabled={disabled}
                    itemRender={DropDownButtonActionRender}
                    onItemClick={onItemClick}
                    text={<Icons iconName="calendar" dimensions={28} />}
                />
            </div>
        </div>
    );

    function onItemClick(e: any) {
        switch (e.item.event) {
            case "delete":
                if (!onChange) return;
                onDateTimeChange(null);
                break;
            case "now":
                onDateTimeChange(moment());
                break;
            default:
                setShow(true);
                break;
        }
    }

    function handleOnChange(e: any) {
        if (!e.value) return;

        const value = e.value;
        const valueMoment = moment(value).local();

        onDateTimeChange(valueMoment);
    }

    function onDateTimeChange(date: any | null) {
        if (date) {

            if (!showSeconds)
                date.set("second", 0);

            date.set("millisecond", 0);

            setLocalValue(date.toDate());

            onResponse(date.toDate());
        } else {
            onResponse();
        }
    }

    function onResponse(e: any | null = null) {
        if (!onChange) return;
        onChange(e);
    }
}
